'use client';

import React from 'react';
import { getApiBaseUrl } from '@/lib/api';
import { getToken } from '@/lib/auth';
import { useTranslations } from '@/hooks/useTranslations';
import { loadRecaptcha, executeRecaptcha, unloadRecaptcha } from '@/lib/recaptcha';

type FormState = {
  name: string;
  email: string;
  subject: string;
  message: string;
};

type Errors = Partial<Record<keyof FormState, string>>;

function getErrorKeys(data: FormState): Partial<Record<keyof FormState, string>> {
  const keys: Partial<Record<keyof FormState, string>> = {};
  if (!data.name.trim()) keys.name = 'contactForm.errorNameRequired';
  else if (data.name.trim().length < 2) keys.name = 'contactForm.errorNameMin';
  else if (data.name.length > 100) keys.name = 'contactForm.errorNameMax';

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!data.email.trim()) keys.email = 'contactForm.errorEmailRequired';
  else if (!emailRegex.test(data.email.trim())) keys.email = 'contactForm.errorEmailInvalid';

  if (!data.subject.trim()) keys.subject = 'contactForm.errorSubjectRequired';
  else if (data.subject.trim().length < 2) keys.subject = 'contactForm.errorSubjectMin';
  else if (data.subject.length > 200) keys.subject = 'contactForm.errorSubjectMax';

  if (!data.message.trim()) keys.message = 'contactForm.errorMessageRequired';
  else if (data.message.trim().length < 10) keys.message = 'contactForm.errorMessageMin';
  else if (data.message.length > 5000) keys.message = 'contactForm.errorMessageMax';

  return keys;
}

function getFieldErrorId(field: keyof FormState) {
  return `contact-${field}-error`;
}

export function ContactForm() {
  const { t } = useTranslations();
  const [form, setForm] = React.useState<FormState>({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isLoggedIn, setIsLoggedIn] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [loadingUser, setLoadingUser] = React.useState(true);
  const [sent, setSent] = React.useState(false);
  const [errors, setErrors] = React.useState<Errors>({});
  const [message, setMessage] = React.useState<string | null>(null);

  React.useEffect(() => {
    loadRecaptcha().catch(() => {});
    return () => unloadRecaptcha();
  }, []);

  React.useEffect(() => {
    const token = getToken();
    if (!token) {
      setLoadingUser(false);
      return;
    }
    fetch(`${getApiBaseUrl()}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((user) => {
        if (user?.email) {
          setForm((prev) => ({
            ...prev,
            name: String(user.displayName ?? '').trim() || prev.name,
            email: String(user.email ?? '').trim() || prev.email,
          }));
          setIsLoggedIn(true);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingUser(false));
  }, []);

  const handleChange = (field: keyof FormState) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    if (isLoggedIn && (field === 'name' || field === 'email')) return;
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
    setMessage(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    const errKeys = getErrorKeys(form);
    if (Object.keys(errKeys).length > 0) {
      const errs: Errors = {};
      for (const [k, key] of Object.entries(errKeys)) {
        if (key) errs[k as keyof FormState] = t(key);
      }
      setErrors(errs);
      return;
    }
    setErrors({});
    setLoading(true);
    try {
      const captchaToken = await executeRecaptcha('contact');
      const res = await fetch(`${getApiBaseUrl()}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          subject: form.subject.trim(),
          message: form.message.trim(),
          captchaToken,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || `Request failed: ${res.status}`);
      }
      setSent(true);
      setForm((prev) => ({
        name: isLoggedIn ? prev.name : '',
        email: isLoggedIn ? prev.email : '',
        subject: '',
        message: '',
      }));
    } catch (err) {
      setMessage(err instanceof Error ? err.message : t('contactForm.submitError'));
    } finally {
      setLoading(false);
    }
  };

  const inputBase =
    'min-h-12 w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-base text-slate-100 placeholder:text-slate-500 transition focus:border-[#ffb74d] focus:outline-none focus:ring-2 focus:ring-[#ffb74d]/35';
  const inputError = 'border-rose-500/50 focus:border-rose-500 focus:ring-rose-500/30';
  const inputDisabled = 'cursor-not-allowed opacity-70';

  if (loadingUser) {
    return (
      <div className="flex items-center justify-center gap-3 py-12 text-sm text-slate-300" role="status" aria-live="polite">
        <span className="h-6 w-6 animate-spin rounded-full border-2 border-[#ffb74d] border-t-transparent" aria-hidden />
        {t('contactForm.loadingUser')}
      </div>
    );
  }

  if (sent) {
    return (
      <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-6 text-center" role="status" aria-live="polite">
        <p className="text-lg font-medium text-emerald-400">{t('contactForm.sentSuccess')}</p>
        <p className="mt-2 text-sm text-slate-400">{t('contactForm.sentDescription')}</p>
        <button
          type="button"
          onClick={() => setSent(false)}
          className="mt-5 inline-flex min-h-11 items-center justify-center rounded-2xl border border-[#ffb74d]/30 bg-[#ffb74d]/10 px-4 py-2 text-sm font-bold text-[#ffd08a] transition hover:bg-[#ffb74d]/15 focus:outline-none focus:ring-2 focus:ring-[#ffb74d]/60"
        >
          {t('contactForm.sendAnother')}
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="contact-name" className="mb-1 block text-sm font-medium text-slate-300">
          {t('contactForm.name')}
        </label>
        <input
          id="contact-name"
          type="text"
          value={form.name}
          onChange={handleChange('name')}
          placeholder={t('contactForm.placeholderName')}
          maxLength={100}
          readOnly={isLoggedIn}
          className={`${inputBase} ${errors.name ? inputError : ''} ${isLoggedIn ? inputDisabled : ''}`}
          aria-invalid={Boolean(errors.name)}
          aria-describedby={errors.name ? getFieldErrorId('name') : undefined}
        />
        {errors.name && (
          <p id={getFieldErrorId('name')} className="mt-1 text-xs text-rose-400" role="alert">
            {errors.name}
          </p>
        )}
      </div>
      <div>
        <label htmlFor="contact-email" className="mb-1 block text-sm font-medium text-slate-300">
          {t('contactForm.email')}
        </label>
        <input
          id="contact-email"
          type="email"
          value={form.email}
          onChange={handleChange('email')}
          placeholder={t('contactForm.placeholderEmail')}
          readOnly={isLoggedIn}
          className={`${inputBase} ${errors.email ? inputError : ''} ${isLoggedIn ? inputDisabled : ''}`}
          aria-invalid={Boolean(errors.email)}
          aria-describedby={[errors.email ? getFieldErrorId('email') : '', isLoggedIn ? 'contact-email-hint' : ''].filter(Boolean).join(' ') || undefined}
        />
        {errors.email && (
          <p id={getFieldErrorId('email')} className="mt-1 text-xs text-rose-400" role="alert">
            {errors.email}
          </p>
        )}
        {isLoggedIn && (
          <p id="contact-email-hint" className="mt-1 text-xs text-slate-500">{t('contactForm.usingAccountEmail')}</p>
        )}
      </div>
      <div>
        <label htmlFor="contact-subject" className="mb-1 block text-sm font-medium text-slate-300">
          {t('contactForm.subject')}
        </label>
        <input
          id="contact-subject"
          type="text"
          value={form.subject}
          onChange={handleChange('subject')}
          placeholder={t('contactForm.placeholderSubject')}
          maxLength={200}
          className={`${inputBase} ${errors.subject ? inputError : ''}`}
          aria-invalid={Boolean(errors.subject)}
          aria-describedby={errors.subject ? getFieldErrorId('subject') : undefined}
        />
        {errors.subject && (
          <p id={getFieldErrorId('subject')} className="mt-1 text-xs text-rose-400" role="alert">
            {errors.subject}
          </p>
        )}
      </div>
      <div>
        <label htmlFor="contact-message" className="mb-1 block text-sm font-medium text-slate-300">
          {t('contactForm.message')}
        </label>
        <textarea
          id="contact-message"
          value={form.message}
          onChange={handleChange('message')}
          placeholder={t('contactForm.placeholderMessage')}
          rows={5}
          maxLength={5000}
          className={`${inputBase} resize-none ${errors.message ? inputError : ''}`}
          aria-invalid={Boolean(errors.message)}
          aria-describedby={[errors.message ? getFieldErrorId('message') : '', 'contact-message-count'].filter(Boolean).join(' ')}
        />
        {errors.message && (
          <p id={getFieldErrorId('message')} className="mt-1 text-xs text-rose-400" role="alert">
            {errors.message}
          </p>
        )}
        <p id="contact-message-count" className="mt-1 text-xs text-slate-500">
          {t('contactForm.charCount', { current: form.message.length, max: 5000 })}
        </p>
      </div>
      {message && (
        <p className="rounded-2xl border border-rose-400/20 bg-rose-400/10 px-4 py-3 text-sm text-rose-100" role="alert">
          {message}
        </p>
      )}
      <button
        type="submit"
        disabled={loading}
        className="inline-flex min-h-12 w-full items-center justify-center rounded-2xl bg-[#ffb74d] px-5 py-3 text-sm font-bold text-[#3a2100] shadow-lg shadow-[#ffb74d]/20 transition hover:bg-[#ffd08a] focus:outline-none focus:ring-2 focus:ring-[#ffb74d] focus:ring-offset-2 focus:ring-offset-[#15192b] disabled:cursor-not-allowed disabled:opacity-60"
      >
        {loading ? t('contactForm.sending') : t('contactForm.sendButton')}
      </button>
    </form>
  );
}
